// pages/AddDevice.jsx
import { useEffect, useMemo, useState, useRef } from "react";
import * as queries from "../graphql/queries"; 
import * as mutations from "../graphql/mutations"; 
import WiFiProvisionModal from "../components/modals/WiFiProvisionModal";
import BuildingModal from "../components/modals/BuildingModal";
import PropertyModal from "../components/modals/PropertyModal";
import UnitModal from "../components/modals/UnitModal";
import { normalizeGraphQLError } from '../utils/graphqlErrors';
import { getFieldError } from '../utils/formErrors';
import { validateDeviceId, formatDeviceIdForDisplay } from '../utils/deviceId';
import { useSearchParams } from "react-router-dom";
import { getAssetTypeOptions } from "../utils/schema/enumUtils.js";
import { useAdminTenant } from "../admin/AdminTenantContext";
import AdminTenantRequired from "../components/admin/AdminTenantRequired";
import { gql } from "../lib/gqlClient";
import {
  fetchProperties,
  fetchBuildingsForProperty,
  fetchUnitsForBuilding,
} from "../lib/LocationLoaders";
import {
  Autocomplete,
  useAuthenticator,
  Alert,
  Button,
  Card,
  Flex,
  Heading,
  View,
  SelectField,
  Text,
  TextField,
  TextAreaField
} from "@aws-amplify/ui-react";

// ---------- LocalStorage helpers (24h TTL) ----------
const LAST_LOC_KEY = "addDevice:lastLocation";
const LAST_LOC_TTL_MS = 24 * 60 * 60 * 1000;

function loadLastLocation() {
  try {
    const raw = localStorage.getItem(LAST_LOC_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > LAST_LOC_TTL_MS) return null;
    return {
      propertyId: parsed.propertyId || "",
      buildingId: parsed.buildingId || "",
      unitId: parsed.unitId || "",
    };
  } catch {
    return null;
  }
}

function saveLastLocation({ propertyId, buildingId, unitId }) {
  try {
    localStorage.setItem(
      LAST_LOC_KEY,
      JSON.stringify({ propertyId, buildingId, unitId, ts: Date.now() })
    );
  } catch {}
}

// ---------- Constants ----------

const assetTypeOptions = getAssetTypeOptions();

export default function AddDevice() {
  const { tenantKey, isPlatformAdmin } = useAdminTenant();

  // Reference lists
  const [properties, setProperties] = useState([]);
  const [buildings, setBuildings] = useState([]);

  // Unit autocomplete
  const [unitQuery, setUnitQuery] = useState("");
  const [allUnits, setAllUnits] = useState([]);
  const [roomSuggestions, setRoomSuggestions] = useState([]);

  const [searchParams] = useSearchParams();
  // Form state
  const [form, setForm] = useState({
    deviceId: "",
    propertyId: "",
    buildingId: "",
    unitId: "",
    room: "",
    assetType: "",
    label: "",
    notes: "",
  });

  // UI state
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingError, setLoadingError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [touched, setTouched] = useState({});

  // Modals
  const [openPropModal, setOpenPropModal] = useState(false);
  const [openBldgModal, setOpenBldgModal] = useState(false);
  const [openUnitModal, setOpenUnitModal] = useState(false);

  const [wifiOpen, setWifiOpen] = useState(false);
  const [wifiCreds, setWifiCreds] = useState({ deviceId: "" });

  const LABEL_MAX = 80;
  const ROOM_MAX = 80;
  const NOTES_MAX = 500;

  const PER_DEVICE_FIELDS = ["deviceId", "unitId", "room", "assetType", "label", "notes"];

  useEffect(() => {
    const rawFromQuery = searchParams.get("device");
    if (!rawFromQuery) return;

    const result = validateDeviceId(rawFromQuery);

    if (result.ok) {
      setForm(f => ({ 
        ...f, 
        deviceId: formatDeviceIdForDisplay(result.deviceId)
      }));

      // Prevent immediate error display
      setTouched(t => ({ ...t, deviceId: false }));
    }

  }, [searchParams]);

  // Prefill with last location
  useEffect(() => {
    const last = loadLastLocation();
    if (last) {
      setForm((f) => ({
        ...f,
        propertyId: last.propertyId || "",
        buildingId: last.buildingId || "",
        unitId: last.unitId || "",
      }));
    }
  }, []);

  async function loadRooms() {
    const result = await gql(
      queries.devicesByTenantCreatedAt, 
      { 
        tenantKey,
        limit: 2000
      }
    );
    const items = result?.devicesByTenantCreatedAt?.items || [];

    const rooms = items
    .map(d => d.room?.trim())
    .filter(Boolean);

    const freq = {};
    for (const r of rooms) {
      freq[r] = (freq[r] || 0) + 1;
    }

    const unique = Array.from(new Set(rooms));
    
    unique.sort((a, b) => {
      return (freq[b] ?? 0) - (freq[a] ?? 0);
    });

    setRoomSuggestions(unique);
  }

  useEffect(() => {
    if (tenantKey) loadRooms();
  }, [tenantKey]);

  // Load properties when tenantKey available
  useEffect(() => {
    (async () => {
      if (!tenantKey) return;
      setLoadingError("");
      try {
        const items = await fetchProperties({ gql, tenantKey });
        setProperties(items);
      } catch (e) {
        console.error(e);
        setLoadingError("Failed to load properties.")
      }
    })();
  }, [tenantKey]);

  const setGlobalError = (message) =>
  setErrors((prev) => ({ ...prev, global: message || null }));

  // Load buildings when property changes
  useEffect(() => {
    (async () => {
      setBuildings([]);
      setAllUnits([]);
      setUnitQuery("");

      if (!tenantKey || !form.propertyId) return;

      setLoadingError(null);

      try {
        const items = await fetchBuildingsForProperty({
          gql,
          tenantKey,
          propertyId: form.propertyId,
        });
        setBuildings(items);
      } catch (e) {
        console.error(e);
        setLoadingError("Failed to load buildings for the selected property.");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantKey, form.propertyId]);

  useEffect(() => {
    (async () => {
      setAllUnits([]);

      if (!tenantKey || !form.buildingId) return;

      try {
        const units = await fetchUnitsForBuilding({
          gql,
          tenantKey,
          buildingId: form.buildingId,
        });
        setAllUnits(units);
      } catch (e) {
        console.error(e);
        setLoadingError("Failed to load units for this building.");
      }
    })();
  }, [tenantKey, form.buildingId]);


  const normalizeUnitCode = (s = "") => 
    s.toString().toLowerCase().replace(/[-\s]/g, "");

  const resolveUnitFromQuery = (query, units) => {
    if (!query) return null;
    const normalizedQuery = normalizeUnitCode(query);
    return units.find(u => normalizeUnitCode(u.code) === normalizedQuery) || null;
  };

  const handleUnitBlur = () => {
    markTouched("unitId");

    if (!unitQuery) {
      // nothing typed: clear selection
      setForm(f => ({ ...f, unitId: "" }));
      return;
    }

    const match = resolveUnitFromQuery(unitQuery, allUnits);

    if (match) {
      // we found a unit that matches the typed code
      setForm(f => ({ ...f, unitId: match.id }));
    }
  };

  // Validation
  const validation = useMemo(() => {
    const v = {};

    const trimmedDid = form.deviceId.trim();
    if (!trimmedDid) {
      v.deviceId = "Required";
    } else {
      const result = validateDeviceId(form.deviceId);
      v.deviceId = result.ok ? null : result.message;
    }

    v.propertyId = form.propertyId ? null : "Required";
    v.buildingId = form.buildingId ? null : "Required";
    v.assetType = form.assetType ? null : "Required";

    if (form.room && form.room.length > ROOM_MAX) {
      v.room = "Room too long";
    }

    if (form.label && form.label.length > LABEL_MAX) {
      v.label = "Label too long";
    }

    if (form.notes && form.notes.length > NOTES_MAX) {
      v.notes = "Notes too long";
    }

    const unitFromId = form.unitId
      ? allUnits.find(u => u.id === form.unitId)
      : null;

    if (form.unitId && !unitFromId) {
      // Invalid ID (should not normally happen)
      v.unitId = "Invalid unit selected.";
      return v;
    }

    if (form.unitId && !unitQuery.trim()) {
      v.unitId =
        "Please select a unit from the list that appears as you type.";
      return v;
    }

    if (unitQuery.trim()) {
      const normalizedQuery = normalizeUnitCode(unitQuery);
      const normalizedFromId = unitFromId
        ? normalizeUnitCode(unitFromId.code)
        : null;

      // If there's no selected unit OR codes don't match
      if (!unitFromId || normalizedQuery !== normalizedFromId) {
        v.unitId =
          "Please select a unit from the list that appears as you type.";
        return v;
      }
    }

    return v;
  }, [form, allUnits, unitQuery]);
  const hasErrors = Object.values(validation).some(Boolean);

  function markTouched(name) {
    setTouched(t => ({ ...t, [name]: true }));
  }

  // Handlers
  const onInput = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSelectProperty = (e) => {
    const propertyId = e.target.value;
    setForm((f) => ({ ...f, propertyId, buildingId: "", unitId: "" }));
    setUnitQuery("");
  };

  const onSelectBuilding = (e) => {
    const buildingId = e.target.value;
    setForm((f) => ({ ...f, buildingId, unitId: "" }));
    setUnitQuery("");
  };

  const onPickUnit = (opt) => {
    setForm((f) => ({ ...f, unitId: opt?.id || "" }));
    setUnitQuery(opt?.code || "");
  };

  const topRef = useRef(null);

  useEffect(() => {
    if (errors?.global || successMsg) {
      topRef.current?.scrollIntoView({ behavior: "smooth" });
    }
  }, [errors?.global, successMsg]);

  function resetPerDeviceState() {
    setForm((f) => {
      const next = { ...f };
      for (const key of PER_DEVICE_FIELDS) {
        next[key] = "";   // or null if you prefer
      }
      return next;
    });

    setTouched((t) => {
      const next = { ...t };
      for (const key of PER_DEVICE_FIELDS) delete next[key];
      return next;
    });

    setUnitQuery("");
    loadRooms();
  }

  // Submit
  const submit = async (e) => {
    e.preventDefault();
    setErrors({});
    setSuccessMsg("");

    if (!tenantKey) {
      setGlobalError("Missing tenantKey; ensure the signed-in user has custom:tenantKey2.");
      return;
    }
    if (hasErrors) {
      setGlobalError("Please fix the highlighted fields.");
      return;
    }

    setSubmitting(true);
    try {
      const input = {
        deviceId: form.deviceId.trim(),
        propertyId: form.propertyId,
        buildingId: form.buildingId,
        unitId: form.unitId || null,
        room: form.room?.trim() || null,
        assetType: form.assetType || null,
        label: form.label?.trim() || null,
        notes: form.notes?.trim() || null,
      };

      const data = await gql(mutations.addDevice, { tenantKey, input });
      console.log("createPendingAddDevice:", data);

      const result = data?.addDevice;
      if (!result) {
        throw new Error("No claimDevice payload returned.");
      }

      const { device, alreadyOnline /*, lastTelemetryAt */ } = result;

      saveLastLocation({ 
        propertyId: form.propertyId, 
        buildingId: form.buildingId, 
        unitId: form.unitId || "" 
      });

      const did = device?.id?.trim() || form.deviceId.trim();
      resetPerDeviceState();

      if (alreadyOnline) {
        setSuccessMsg("Device claimed successfully. It’s already online.");
      } else {
        setWifiCreds({ deviceId: formatDeviceIdForDisplay(did) });
        setWifiOpen(true);
      }
    } catch (e2) {
      console.error(e2);
      const mapped = normalizeGraphQLError(e2);
      setErrors(mapped)
    } finally {
      setSubmitting(false);
    }
  };

  // Create modals submitters
  const addProperty = async (payload) => {
    // payload from modal: { name, code, address, notes }
    const inputObj = {tenantKey, input: payload};
    const data = await gql(mutations.addProperty, inputObj);
    const p = data?.addProperty ?? { ...payload };  // fallback if resolver echoes
    return p;                                        // <-- no setState here
  };

  const handlePropertyCreated = (p) => {
    // merge + sort property list
    setProperties((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      map.set(p.id, p);
      return [...map.values()].sort((a, b) => a.name.localeCompare(b.name));
    });

    // select property and clear building/unit selections
    setForm((f) => ({ ...f, propertyId: p.id, buildingId: "", unitId: "" }));
  };

  const addBuilding = async (payload) => {
    // input from modal: { id, propertyId, code, name?, notes? }
    const inputObj = {tenantKey, input: payload};
    console.log("input:", inputObj);
    const data = await gql(mutations.addBuilding, inputObj);
    const b = data?.addBuilding ?? null;
    if (!b) throw new Error('No createBuilding payload returned.');
    return b;
  };

  // Merge + select
  const handleBuildingCreated = (b) => {
    // Merge into buildings list
    setBuildings((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      map.set(b.id, b);
      return [...map.values()].sort((a, c) =>
        (a.code).localeCompare(c.code)
      );
    });

    // Ensure the chosen property is selected; clear unit after building change
    setForm((f) => ({
      ...f,
      buildingId: b.id,
      unitId: "",
    }));
  };

  const selectedProperty = useMemo(
    () => properties.find(p => p.id === form.propertyId) || null,
    [properties, form.propertyId]
  );

  const addUnit = async (input) => {
    // input: { propertyId, buildingId, code, name?, notes? }
    const data = await gql(mutations.addUnit, { tenantKey, input });
    const u = data?.addUnit ?? null;
    if (!u) throw new Error('No addUnit payload returned.');
    return u;
  };

  const handleUnitCreated = (u) => {
    setAllUnits((prev) => {
      const map = new Map(prev.map((x) => [x.id, x]));
      map.set(u.id, u);
      return [...map.values()].sort((a, c) => (a.code || '').localeCompare(c.code || ''));
    });

    onPickUnit(u);
  };

  const selectedBuilding = useMemo(
    () => buildings.find(b => b.id === form.buildingId) || null,
    [buildings, form.buildingId]
  );

  const deviceIdError = getFieldError({
    field: "deviceId",
    touched,
    validation,
    errors
  });

  const propertyIdError = getFieldError({
    field: "propertyId",
    touched,
    validation,
    errors
  });

  const buildingIdError = getFieldError({
    field: "buildingId",
    touched,
    validation,
    errors
  });

  const unitIdError = getFieldError({
    field: "unitId",
    touched,
    validation,
    errors
  });

  const roomError = getFieldError({
    field: "room",
    touched,
    validation,
    errors
  });

  const assetTypeError = getFieldError({
    field: "assetType",
    touched,
    validation,
    errors
  });

  const labelError = getFieldError({
    field: "label",
    touched,
    validation,
    errors
  });

  const notesError = getFieldError({
    field: "notes",
    touched,
    validation,
    errors
  });

  if (isPlatformAdmin && !tenantKey) {
    return (
      <AdminTenantRequired
        title="Add Device"
        message="Select a tenant in the top bar before creating anything."
      />
    );
  }

  return (
  <View maxWidth="48rem" margin="0 auto" padding="1.5rem">
    <Heading level={3}>Add Device</Heading>
    <div ref={topRef} />
    {successMsg && (
      <Alert
        variation="success"
        marginTop="1rem"
        hasIcon
        isDismissible={true}
        onDismiss={() => setSuccessMsg("")}
      >
        {successMsg}
      </Alert>
    )}
    {errors.global && (
      <Alert
        variation="error"
        isDismissible={true}
        onDismiss={() => setErrors((e) => ({ ...e, global: undefined }))}
        heading="Couldn't submit form"
      >
        {errors.global}
      </Alert>
    )}
    {loadingError && (
      <Alert
        variation="error"
        marginTop="1rem"
        hasIcon
        isDismissible={false}
      >
        {loadingError}
      </Alert>
    )}

    <View as="form" onSubmit={submit} marginTop="1.5rem">
      <Flex direction="column" gap="1.25rem">
        <Card variation="elevated">
          <Heading level={5} marginBottom="0.75rem">
            Device
          </Heading>

          <Flex direction="column" gap="1.25rem">
            {/* Device ID */}
            <TextField
              label="Device ID"
              name="deviceId"
              descriptiveText="Scan the QR code on your device or type it in here."
              value={form.deviceId}
              onChange={(e) => {
                onInput(e);
                if (errors.deviceId) setErrors(prev => ({ ...prev, deviceId: undefined, global: undefined }));
              }}
              placeholder="ABCD-EFGH-J"
              autoComplete="off"
              onBlur={() => markTouched("deviceId")}
              hasError={!!deviceIdError}
              errorMessage={deviceIdError || undefined}
            />

            {/* Label */}
            <TextField
              label="Label (optional)"
              name="label"
              value={form.label}
              onBlur={() => markTouched("label")}
              hasError={!!labelError}
              errorMessage={labelError || undefined}
              onChange={onInput}
              placeholder="Friendly name"
            />
          </Flex>
        </Card>

        <Card variation="elevated">
          <Heading level={5} marginBottom="0.75rem">
            Location
          </Heading>

          <Flex direction="column" gap="1.25rem">
          {/* Property */}
            <View>
              <SelectField
                label="Property"
                name="propertyId"
                value={form.propertyId}
                onChange={onSelectProperty}
                marginTop="0.25rem"
                onBlur={() => markTouched("propertyId")}
                hasError={!!propertyIdError}
                errorMessage={propertyIdError || undefined}
              >
                <option value="">Select a property…</option>
                {properties.map((p) => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </SelectField>
              <Flex alignItems="center" justifyContent="space-between">
                <Button
                  type="button"
                  size="small"
                  variation="link"
                  onClick={() => setOpenPropModal(true)}
                >
                  + New Property
                </Button>
              </Flex>
            </View>

            {/* Building */}
            <View>
              <SelectField
                label="Building"
                name="buildingId"
                value={form.buildingId}
                onChange={onSelectBuilding}
                isDisabled={!form.propertyId}
                marginTop="0.25rem"
                onBlur={() => markTouched("buildingId")}
                hasError={!!buildingIdError}
                errorMessage={buildingIdError || undefined}
              >
                <option value="">
                  {form.propertyId ? "Select a building…" : "Select a property first"}
                </option>
                {buildings.map((b) => (
                  <option key={b.id} value={b.id}>
                    {/* show code — name if you mapped it earlier; here just name */}
                    {b.code}{b.name ? ` — ${b.name}` : ""}
                  </option>
                ))}
              </SelectField>
              <Flex alignItems="center" justifyContent="space-between">
                <Button
                  type="button"
                  size="small"
                  variation="link"
                  isDisabled={!form.propertyId}
                  onClick={() => setOpenBldgModal(true)}
                  ariaLabel={form.propertyId ? "Add new building for selected property" : "Select a property first"}
                  title={form.propertyId ? "Add new building for selected property" : "Select a property first"}
                >
                  + New Building (for selected property)
                </Button>
              </Flex>
            </View>

            {/* Unit (client-side type-ahead over preloaded list) */}
            <View>
              <Autocomplete
                label="Unit"
                labelHidden={false}
                placeholder={
                  form.buildingId
                    ? "Type to search units (e.g., C-12)..."
                    : "Select a building first"
                }
                isDisabled={!form.buildingId}
                onChange={(event) => {
                  setUnitQuery(event.target.value);
                }}
                options={allUnits.map(u => ({
                  id: u.id,
                  label: u.code
                }))}
                onSelect={(option) => {
                  setForm((f) => ({ ...f, unitId: option?.id || "" }));
                  setUnitQuery(option?.label || "");
                }}
                onClear={() => {
                  setForm((f) => ({ ...f, unitId: "" }));
                  setUnitQuery('');
                }}
                value={unitQuery}
                onBlur={handleUnitBlur}
                hasError={!!unitIdError}
                errorMessage={unitIdError || undefined}
                />
                <Flex alignItems="center" justifyContent="space-between">
                  <Button
                    type="button"
                    size="small"
                    variation="link"
                    isDisabled={!form.buildingId}
                    onClick={() => setOpenUnitModal(true)}
                    ariaLabel={form.buildingId ? "Add new unit for selected property and building" : "Select a building first"}
                    title={form.buildingId ? "Add new unit for selected property and building" : "Select a building first"}
                  >
                    + New Unit (for selected property and building)
                  </Button>
                </Flex>
            </View>
          </Flex>
        </Card>

        <Card variation="elevated">
          <Heading level={5} marginBottom="0.75rem">
            Asset & Details
          </Heading>

          <Flex direction="column" gap="1.25rem">
            {/* Asset Type */}
            <SelectField
              label="Asset Type"
              name="assetType"
              value={form.assetType}
              onBlur={() => markTouched("assetType")}
              hasError={!!assetTypeError}
              errorMessage={assetTypeError || undefined}
              onChange={onInput}
            >
              <option value="">Select type…</option>
              {assetTypeOptions.map((t) => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </SelectField>

            {/* Room */}
            <TextField
              label="Room / Location (optional)"
              name="room"
              value={form.room}
              onBlur={() => markTouched("room")}
              hasError={!!roomError}
              errorMessage={roomError || undefined}
              onChange={onInput}
              placeholder="e.g., Bathroom, Laundry closet"
              spellCheck={true}
              list="room-suggestions"
            />
            <datalist id="room-suggestions">
              {roomSuggestions.map((r) => (
                <option key={r} value={r} />
              ))}
            </datalist>

            {/* Notes */}
            <TextAreaField
              label="Notes (optional)"
              name="notes"
              value={form.notes}
              onBlur={() => markTouched("notes")}
              hasError={!!notesError}
              errorMessage={notesError || undefined}
              onChange={onInput}
              rows={3}
              placeholder="Anything helpful for maintenance…"
            />
          </Flex>
        </Card>

        {/* Actions */}
        <Flex alignItems="center" gap="0.75rem">
          <Button
            type="submit"
            isDisabled={!tenantKey || submitting || hasErrors}
            variation={(!tenantKey || submitting || hasErrors) ? "secondary" : "primary"}
          >
            {submitting ? "Submitting…" : "Claim Device"}
          </Button>
        </Flex>
      </Flex>
    </View>

    {/* -------- Modals (Headless UI wrapper, Amplify UI inputs inside) -------- */}
    <PropertyModal
      open={openPropModal}
      onClose={() => setOpenPropModal(false)}
      createPropertyFn={addProperty}
      onCreated={(p) => {
        handlePropertyCreated(p);
        setOpenPropModal(false);
      }}
    />

    <BuildingModal
      open={openBldgModal}
      onClose={() => setOpenBldgModal(false)}
      createBuildingFn={addBuilding}
      buildingProperty={selectedProperty}
      onCreated={(b) => {
        handleBuildingCreated(b);
        setOpenBldgModal(false);
      }}
    />

    <UnitModal
      open={openUnitModal}
      onClose={() => setOpenUnitModal(false)}
      createUnitFn={addUnit}
      unitProperty={selectedProperty}
      unitBuilding={selectedBuilding}
      onCreated={(u) => {
        handleUnitCreated(u);
        setOpenUnitModal(false);
      }}
    />
    <WiFiProvisionModal
      open={wifiOpen}
      onClose={() => setWifiOpen(false)}
      deviceId={wifiCreds.deviceId}
    />
  </View>
);

}
